/**
 * Sign-in: redirect, with a mandatory flush.
 *
 * Redirect rather than popup, decided in docs/decisions/sign-in.md. The subtle
 * part: popup blockers fire on anything not synchronously inside a click, and
 * our flush is async — so a popup would be blocked *because* we did the safe
 * thing first. Redirect has one failure mode, and the flush closes it.
 *
 * The flush is a precondition, not a best effort. An OAuth redirect destroys
 * in-memory state, so if unsaved strokes cannot be persisted we do not leave.
 */
import {
  callbackUrlFor,
  createIntent,
  type IntentKind,
  newNonce,
  type PendingIntent,
  resumeIntent,
} from "../../shared/pending-intent";
import { rememberAttempt } from "./device-memory";
import { announceChange, apiFetch, refresh } from "./session";

const INTENT_KEY = "rcm.pending-intent";

export type Provider = "discord" | "google";

export class FlushFailed extends Error {
  constructor(cause: unknown) {
    super(`Could not save your work before signing in: ${String(cause)}`);
    this.name = "FlushFailed";
  }
}

type Flush = () => Promise<void>;
let flush: Flush = async () => {};

/**
 * The editor registers how to persist unsaved work. Called before every
 * redirect; if it rejects, the redirect is abandoned.
 */
export function registerFlush(fn: Flush) {
  flush = fn;
}

function storeIntent(intent: PendingIntent) {
  sessionStorage.setItem(INTENT_KEY, JSON.stringify(intent));
}

function readIntent(): PendingIntent | null {
  const raw = sessionStorage.getItem(INTENT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingIntent;
  } catch {
    // A corrupted record is discarded rather than trusted.
    sessionStorage.removeItem(INTENT_KEY);
    return null;
  }
}

export function clearIntent() {
  sessionStorage.removeItem(INTENT_KEY);
}

export interface SignInOptions {
  kind?: IntentKind;
  projectId?: string;
  /** Same-origin path to land on. Sanitised; absolute URLs are rejected. */
  returnTo?: string;
}

/** The subset of `location` this needs — so it can be called with a plain object. */
export interface LocationLike {
  protocol: string;
  host: string;
  pathname: string;
  search: string;
}

/**
 * The https URL to move to before signing in, or null if already fine.
 *
 * Sign-in over plain http is not something to make work — it is something to
 * stop. The session cookie is Secure, so the round trip cannot complete anyway;
 * Web Crypto is half-absent, which is how this surfaced as a bare
 * "crypto.randomUUID is not a function" with no other clue; and the OAuth
 * redirect would carry the flow across an unencrypted hop.
 *
 * Keyed on `protocol`, not `isSecureContext`: http://localhost IS a secure
 * context, and is the one place this must never fire — upgrading it would send
 * developers to an https port that is not listening.
 *
 * The zone's "Always Use HTTPS" is the real fix and lives in Cloudflare, not
 * here. This is the guard for when that is off — exactly the state that
 * produced the bug report.
 */
export function secureUrlFor(loc: LocationLike): string | null {
  if (loc.protocol !== "http:") return null;
  const host = loc.host.replace(/:\d+$/, "");
  if (host === "localhost" || host === "127.0.0.1" || host === "[::1]") return null;
  return `https://${loc.host}${loc.pathname}${loc.search}`;
}

/**
 * Begin sign-in. Does not return — the page navigates away — unless it throws.
 */
export async function signIn(provider: Provider, opts: SignInOptions = {}): Promise<never> {
  // Insecure origin: upgrade and stop here. See secureUrlFor for why.
  const secure = secureUrlFor(location);
  if (secure) {
    location.replace(secure);
    return new Promise<never>(() => {});
  }

  try {
    await flush();
  } catch (err) {
    throw new FlushFailed(err);
  }

  const intent = createIntent(opts.kind ?? "none", {
    projectId: opts.projectId,
    returnTo: opts.returnTo ?? location.pathname + location.search,
    nonce: newNonce(),
    now: Date.now(),
  });
  storeIntent(intent);

  // Recorded as an attempt only. It becomes the remembered provider in
  // session.refresh(), once a real session proves the round trip worked.
  rememberAttempt(provider);

  const res = await apiFetch("/api/auth/sign-in/social", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ provider, callbackURL: callbackUrlFor(intent) }),
  });

  if (!res.ok) {
    clearIntent();
    throw new Error(`Sign-in with ${provider} is unavailable right now.`);
  }

  const { url } = (await res.json()) as { url?: string };
  if (!url) {
    clearIntent();
    throw new Error(`Sign-in with ${provider} is unavailable right now.`);
  }

  location.href = url;
  // Navigation is asynchronous; block so callers cannot continue mid-teardown.
  return new Promise<never>(() => {});
}

/**
 * Sign out.
 *
 * Clears the session and nothing else. Local projects stay in IndexedDB — a
 * sign-out that wipes local storage is indistinguishable from data loss, and
 * people sign out on shared machines precisely when they can least afford it.
 */
export async function signOut(): Promise<void> {
  await apiFetch("/api/auth/sign-out", { method: "POST" });
  clearIntent();
  await refresh();
  announceChange();
}

export interface Resumed {
  intent: PendingIntent;
}

/**
 * Call once on load. Returns the intent to resume, or null.
 *
 * Consumes the intent and strips the auth params from the URL either way, so a
 * refresh cannot replay it.
 */
export function consumeResume(now = Date.now()): Resumed | null {
  const params = new URLSearchParams(location.search);
  if (params.get("auth") !== "return") return null;

  const check = resumeIntent(readIntent(), params.get("n"), now);
  clearIntent();

  params.delete("auth");
  params.delete("n");
  const query = params.toString();
  history.replaceState({}, "", location.pathname + (query ? `?${query}` : ""));

  return check.ok && check.intent ? { intent: check.intent } : null;
}
